import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import moment from 'moment';
import { makeStyles } from '@mui/styles';
import {
	Card, CardActions, CardContent, CardHeader, Grid, Divider,
	colors, List, ListItem, ListItemText, Typography, IconButton,
	Input, Paper, Tooltip, Accordion, AccordionActions, AccordionSummary,
	AccordionDetails, Button, TextField, Stack, AccordionDetail
} from '@mui/material';
import DatePicker from 'components/DatePicker/DatePicker.js';
import DateTimePicker from 'components/DatePicker/DateTimePicker.js';
import { SelectField } from 'components';
import SendIcon from '@mui/icons-material/Send';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
	SetFormState, AddComment, ToggleAttachFileSiteTrackerDialog,
	DownloadAttachments
} from '../../../../../actions/SiteTrackerActions';
import SiteTrackerComments from '../SiteTrackerComments';
import TeamsFilesDatatable from './components/TeamsFilesDatatable';
import './Teams.css';

const useStyles = makeStyles((theme) => ({
	root: {
		marginTop: theme.spacing(1),
	},
	generate: {
		color: theme.palette.white,
		backgroundColor: colors.green[600],
		'&:hover': {
			backgroundColor: colors.green[900],
		},
	},
	paper: {
		flexGrow: 1,
		padding: theme.spacing(0.5, 2),
	},
	input: {
		width: '100%',
	},
}));

const TeamOne = (props) => {
	const { className, tableId, attachments, comments, ...rest } = props;
	const classes = useStyles();
	const dispatch = useDispatch();
	const userId = useSelector(state => state.session.user.id);
	const setFormState = formState => dispatch(SetFormState(formState));
	const formState = useSelector((state) => state.siteTrackerReducer.formState);
	const _hasError = (field) => (!!(formState.touched[field] && formState.errors[field]));
	const dropdowns = useSelector(state => state.siteTrackerReducer.dropdowns);

	// Attachments
	// const [attachmentFiles, setAttachmentFiles] = useState();
	// const [attachmentImage, setAttachmentImage] = useState();

	// useMemo(() => {
	// 	setAttachmentFiles(attachments && attachments.attachmentFiles);
	// 	setAttachmentImage(attachments && attachments.attachmentImage);
	// }, [attachments]);

	const _onAddAttachmentsClick = (title) => {
		dispatch(ToggleAttachFileSiteTrackerDialog(true, tableId, title));
	};

	const _onDownloadAttachmentsClick = (title) => {
		dispatch(DownloadAttachments(userId, tableId, title));
	};

	// const _onRemoveAttachmentsClick = (title) => {
	// 	dispatch(ToggleRemoveFileSiteTrackerDialog(true, tableId, title));
	// };

	const _handleChange = event => {

		setFormState({
			...formState,
			values: {
				...formState.values,
				[event.target.name]:
					event.target.type === 'checkbox'
						? event.target.checked
						: event.target.value
			},
			touched: {
				...formState.touched,
				[event.target.name]: true
			}
		});

		rulesManagement(event.target.name, event.target.value);
	};

	const _handleDateChange = (value, name, type = 'date') => {
		let formattedValue = null;

		if (value) {
			if (type === 'date') {
				formattedValue = value.startOf('day').format('YYYY-MM-DD');
			} else {
				formattedValue = value.format('YYYY-MM-DD HH:mm:ss');
			}
		}

		setFormState({
			...formState,
			values: {
				...formState.values,
				[name]: formattedValue
			},
			touched: {
				...formState.touched,
				[name]: true
			}
		});
	};

	const rulesManagement = (fieldName, value) => {

		const datetimeNow = moment().format('YYYY-MM-DD HH:mm:ss');

		if (fieldName === 'currentStatus') {
			if (value === 'Completed') {
				setFormState({
					...formState,
					values: {
						...formState.values,
						[fieldName]: value,
						deliveredDate: datetimeNow,
						claimsStatusUser: userId,
						claimsStatusDate: datetimeNow,
						claimsStatus: 'Done'
					},
					touched: {
						...formState.touched,
						[fieldName]: true,
					}
				});
			} else {
				setFormState({
					...formState,
					values: {
						...formState.values,
						[fieldName]: value,
						deliveredDate: null
					},
					touched: {
						...formState.touched,
						[fieldName]: true,
						deliveredDate: true
					}
				});
			}
		}
	};

	return (
		<Card {...rest} className={clsx(classes.root, className)}>
			<div>
				<Accordion>
					<AccordionSummary
						expandIcon={<ExpandMoreIcon />}
						aria-controls="panel1-content"
						id="panel1-header"
						className='highlight'
					>
						<Typography variant="h5" gutterBottom component="div">Work Status</Typography>
					</AccordionSummary>
					<AccordionDetails className="backgroundGrey" style={{ paddingTop: '20px' }}>
						<Grid container spacing={2}>
							<Grid item md={3} xs={3}>
								<DatePicker
									id="dateEntryInPlan"
									name="dateEntryInPlan"
									label="Date Entry in Plan"
									value={formState.values.dateEntryInPlan}
									onChange={value => _handleDateChange(value, 'dateEntryInPlan')}
									error={
										_hasError('dateEntryInPlan')
											? formState.errors.dateEntryInPlan[0]
											: null
									}
									helperText={
										_hasError('dateEntryInPlan')
											? formState.errors.dateEntryInPlan[0]
											: null
									}
									format="DD-MM-YYYY"
								/>
							</Grid>
							<Grid item md={3} xs={3}>
								<TextField
									id="dayCount"
									name="dayCount"
									label="Day Count"
									variant="outlined"
									error={_hasError('dayCount')}
									fullWidth
									helperText={
										_hasError('dayCount')
											? formState.errors.dayCount[0]
											: null
									}
									value={formState.values.dayCount || ''}
									onChange={_handleChange}
									style={{ backgroundColor: 'white' }}
								/>
							</Grid>
							<Grid item md={3} xs={3}>
								<SelectField
									id="currentStatus"
									name="currentStatus"
									hintText="Current Status"
									defaultValue=""
									menuItems={
										(dropdowns && dropdowns.currentStatus) || []
									}
									value={formState.values.currentStatus}
									onChange={_handleChange}
									error={
										_hasError('currentStatus')
											? formState.errors.currentStatus[0]
											: null
									}
									style={{ backgroundColor: 'white', border: '1px solid #d5d9e0' }}
								/>
							</Grid>
							<Grid item md={3} xs={3}>
								<DateTimePicker
									id="deliveredDate"
									name="deliveredDate"
									label="Delivered Date"
									value={formState.values.deliveredDate}
									onChange={value => _handleDateChange(value, 'deliveredDate', 'time')}
									error={
										_hasError('deliveredDate')
											? formState.errors.deliveredDate[0]
											: null
									}
									helperText={
										_hasError('deliveredDate')
											? formState.errors.deliveredDate[0]
											: null
									}
									minutesStep={1}
									format="DD/MM/YYYY HH:mm"
								/>
							</Grid>
						</Grid>
						<br />
						<Grid container spacing={2}>
							<Grid item md={12} xs={12}>
								<SiteTrackerComments title="Work Status Comments" tableId={tableId} comments={comments.workStatus} />
							</Grid>
						</Grid>
						{/* New row */}
						<br />
						<Grid container spacing={2}>
							<Grid item md={12} xs={12}>
								<Divider><Typography variant="h5" gutterBottom component="div">Attachments</Typography></Divider>
							</Grid>
							<Grid item md={10} xs={10}>
								<TeamsFilesDatatable data={(attachments && attachments.workStatus) && attachments.workStatus} tableId={tableId} />
							</Grid>
							<Grid item md={2}>
								<Grid container spacing={2}>
									<Grid item md={12} xs={12}>
										<Button
											className={classes.agreeButton}
											color="primary"
											onClick={() => _onAddAttachmentsClick('workStatus')}
											variant="contained"
											size="small"
											style={{ width: '100%' }}
										>
											Attach Files
										</Button>
									</Grid>
									{/* <Grid item md={12} xs={12} hidden={attachments && attachments.workStatus === null}>
										<Button
											className={classes.agreeButton}
											color="primary"
											onClick={() => _onRemoveAttachmentsClick('workStatus')}
											variant="contained"
											size="small"
											style={{ width: '100%' }}
										>
											Delete All Files
										</Button>
									</Grid> */}
									<Grid item md={12} xs={12}>
										<Button
											className={classes.agreeButton}
											color="primary"
											onClick={() => _onDownloadAttachmentsClick('workStatus')}
											variant="contained"
											size="small"
											style={{ width: '100%' }}
											disabled={(attachments && attachments.workStatus) && attachments.workStatus.length === 0}
										>
											Download All Files
										</Button>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</AccordionDetails>
				</Accordion>
				<Accordion>
					<AccordionSummary
						expandIcon={<ExpandMoreIcon />}
						aria-controls="panel2-content"
						id="panel2-header"
						className='highlight'
					>
						<Typography variant="h5" gutterBottom component="div">VMO2 Performance</Typography>
					</AccordionSummary>
					<AccordionDetails className="backgroundGrey" style={{ paddingTop: '20px' }}>
						<Grid container spacing={2}>
							<Grid item md={2} xs={2}>
								<SelectField
									id="vendor"
									name="vendor"
									hintText="Vendor"
									defaultValue=""
									menuItems={
										(dropdowns && dropdowns.vendor) || []
									}
									value={formState.values.vendor}
									onChange={_handleChange}
									error={
										_hasError('vendor')
											? formState.errors.vendor[0]
											: null
									}
									style={{ backgroundColor: 'white', border: '1px solid #d5d9e0' }}
									disabled
								/>
							</Grid>
							<Grid item md={3} xs={3}>
								<DatePicker
									id="kpisMonitorVmo26hDate"
									name="kpisMonitorVmo26hDate"
									label="KPIs Monitor VMO2 (6h) Date"
									value={formState.values.kpisMonitorVmo26hDate}
									onChange={value => _handleDateChange(value, 'kpisMonitorVmo26hDate')}
									error={
										_hasError('kpisMonitorVmo26hDate')
											? formState.errors.kpisMonitorVmo26hDate[0]
											: null
									}
									helperText={
										_hasError('kpisMonitorVmo26hDate')
											? formState.errors.kpisMonitorVmo26hDate[0]
											: null
									}
									format="DD-MM-YYYY"
								/>
							</Grid>
							<Grid item md={3} xs={3}>
								<SelectField
									id="kpisUserVmo26h"
									name="kpisUserVmo26h"
									hintText="KPIs User VMO2 (6h)"
									defaultValue=""
									menuItems={
										(dropdowns && dropdowns.userName) || []
									}
									value={formState.values.kpisUserVmo26h}
									onChange={_handleChange}
									error={
										_hasError('kpisUserVmo26h')
											? formState.errors.kpisUserVmo26h[0]
											: null
									}
									style={{ backgroundColor: 'white', border: '1px solid #d5d9e0' }}
								/>
							</Grid>
							<Grid item md={2} xs={2}>
								<SelectField
									id="_2gStatusVmo26h"
									name="_2gStatusVmo26h"
									hintText="2G Status VMO2 (6h)"
									defaultValue=""
									menuItems={
										(dropdowns && dropdowns.okNokStatus) || []
									}
									value={formState.values._2gStatusVmo26h}
									onChange={_handleChange}
									error={
										_hasError('_2gStatusVmo26h')
											? formState.errors._2gStatusVmo26h[0]
											: null
									}
									style={{ backgroundColor: 'white', border: '1px solid #d5d9e0' }}
								/>
							</Grid>
							<Grid item md={2} xs={2}>
								<SelectField
									id="_3gStatusVmo26h"
									name="_3gStatusVmo26h"
									hintText="3G Status VMO2 (6h)"
									defaultValue=""
									menuItems={
										(dropdowns && dropdowns.okNokStatus) || []
									}
									value={formState.values._3gStatusVmo26h}
									onChange={_handleChange}
									error={
										_hasError('_3gStatusVmo26h')
											? formState.errors._3gStatusVmo26h[0]
											: null
									}
									style={{ backgroundColor: 'white', border: '1px solid #d5d9e0' }}
								/>
							</Grid>
						</Grid>
						<br />
						<Grid container spacing={2}>
							<Grid item md={2} xs={2}>
								<SelectField
									id="_4gStatusVmo26h"
									name="_4gStatusVmo26h"
									hintText="4G Status VMO2 (6h)"
									defaultValue=""
									menuItems={
										(dropdowns && dropdowns.okNokStatus) || []
									}
									value={formState.values._4gStatusVmo26h}
									onChange={_handleChange}
									error={
										_hasError('_4gStatusVmo26h')
											? formState.errors._4gStatusVmo26h[0]
											: null
									}
									style={{ backgroundColor: 'white', border: '1px solid #d5d9e0' }}
								/>
							</Grid>
							<Grid item md={2} xs={2}>
								<SelectField
									id="_5gStatusVmo26h"
									name="_5gStatusVmo26h"
									hintText="5G Status VMO2 (6h)"
									defaultValue=""
									menuItems={
										(dropdowns && dropdowns.okNokStatus) || []
									}
									value={formState.values._5gStatusVmo26h}
									onChange={_handleChange}
									error={
										_hasError('_5gStatusVmo26h')
											? formState.errors._5gStatusVmo26h[0]
											: null
									}
									style={{ backgroundColor: 'white', border: '1px solid #d5d9e0' }}
								/>
							</Grid>
							<Grid item md={3} xs={3}>
								<SelectField
									id="kPIsMonitoringStatusVmo26h"
									name="kPIsMonitoringStatusVmo26h"
									hintText="KPIs Monitoring Status VMO2 (6h) "
									defaultValue=""
									menuItems={
										(dropdowns && dropdowns.okNokStatus) || []
									}
									value={formState.values.kPIsMonitoringStatusVmo26h}
									onChange={_handleChange}
									error={
										_hasError('kPIsMonitoringStatusVmo26h')
											? formState.errors.kPIsMonitoringStatusVmo26h[0]
											: null
									}
									style={{ backgroundColor: 'white', border: '1px solid #d5d9e0' }}
								/>
							</Grid>
							<Grid item md={5} xs={5}>
								<TextField
									id="KpisVmo26hObs"
									name="KpisVmo26hObs"
									label="KPIs VMO2 (6h) Obs"
									variant="outlined"
									error={_hasError('KpisVmo26hObs')}
									fullWidth
									helperText={
										_hasError('KpisVmo26hObs')
											? formState.errors.KpisVmo26hObs[0]
											: null
									}
									value={formState.values.KpisVmo26hObs || ''}
									onChange={_handleChange}
									sx={{
										'& .MuiInputBase-input': {
											backgroundColor: 'white',
											'&.Mui-disabled': {
												color: 'dimGray',
												WebkitTextFillColor: 'dimGray'
											}
										},
									}}
								/>
							</Grid>
						</Grid>
						<br />
						<Grid container spacing={2}>
							<Grid item md={12} xs={12}>
								<SiteTrackerComments title="VMO2 Performance Comments" tableId={tableId} comments={comments.vmo2Performance} />
							</Grid>
						</Grid>
						{/* New row */}
						<br />
						<Grid container spacing={2}>
							<Grid item md={12} xs={12}>
								<Divider><Typography variant="h5" gutterBottom component="div">Attachments</Typography></Divider>
							</Grid>
							<Grid item md={10} xs={10}>
								<TeamsFilesDatatable data={(attachments && attachments.vmo2Performance) && attachments.vmo2Performance} tableId={tableId} />
							</Grid>
							<Grid item md={2}>
								<Grid container spacing={2}>
									<Grid item md={12} xs={12}>
										<Button
											className={classes.agreeButton}
											color="primary"
											onClick={() => _onAddAttachmentsClick('vmo2Performance')}
											variant="contained"
											size="small"
											style={{ width: '100%' }}
										>
											Attach Files
										</Button>
									</Grid>
									{/* <Grid item md={12} xs={12} hidden={attachments && attachments.vmo2Performance === null}>
										<Button
											className={classes.agreeButton}
											color="primary"
											onClick={() => _onRemoveAttachmentsClick('vmo2Performance')}
											variant="contained"
											size="small"
											style={{ width: '100%' }}
										>
											Delete All Files
										</Button>
									</Grid> */}
									<Grid item md={12} xs={12}>
										<Button
											className={classes.agreeButton}
											color="primary"
											onClick={() => _onDownloadAttachmentsClick('vmo2Performance')}
											variant="contained"
											size="small"
											style={{ width: '100%' }}
											disabled={(attachments && attachments.vmo2Performance) && attachments.vmo2Performance.length === 0}
										>
											Download All Files
										</Button>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</AccordionDetails>
				</Accordion>
				<Accordion>
					<AccordionSummary
						expandIcon={<ExpandMoreIcon />}
						aria-controls="panel2-content"
						id="panel2-header"
						className='highlight'
					>
						<Typography variant="h5" gutterBottom component="div">VFUK/3UK Performance</Typography>
					</AccordionSummary>
					<AccordionDetails className="backgroundGrey" style={{ paddingTop: '20px' }}>
						<Grid container spacing={2}>
							<Grid item md={2} xs={2}>
								<SelectField
									id="host"
									name="host"
									hintText="Host"
									defaultValue=""
									menuItems={
										(dropdowns && dropdowns.host) || []
									}
									value={formState.values.host}
									onChange={_handleChange}
									error={
										_hasError('host')
											? formState.errors.host[0]
											: null
									}
									style={{ backgroundColor: 'white', border: '1px solid #d5d9e0' }}
									disabled
								/>
							</Grid>
							<Grid item md={3} xs={3}>
								<DatePicker
									id="kpisMonitoringDateVfuk3uk48h"
									name="kpisMonitoringDateVfuk3uk48h"
									label="KPIs Monitoring Date VFUK/3UK (48h)"
									value={formState.values.kpisMonitoringDateVfuk3uk48h}
									onChange={value => _handleDateChange(value, 'kpisMonitoringDateVfuk3uk48h')}
									error={
										_hasError('kpisMonitoringDateVfuk3uk48h')
											? formState.errors.kpisMonitoringDateVfuk3uk48h[0]
											: null
									}
									helperText={
										_hasError('kpisMonitoringDateVfuk3uk48h')
											? formState.errors.kpisMonitoringDateVfuk3uk48h[0]
											: null
									}
									format="DD-MM-YYYY"
								/>
							</Grid>
						</Grid>
						<br />
						<Grid container spacing={2}>
							<Grid item md={2} xs={2}>
								<SelectField
									id="kpisUserVfuk48h"
									name="kpisUserVfuk48h"
									hintText="KPIs User VFUK (48h)"
									defaultValue=""
									menuItems={
										(dropdowns && dropdowns.userName) || []
									}
									value={formState.values.kpisUserVfuk48h}
									onChange={_handleChange}
									error={
										_hasError('kpisUserVfuk48h')
											? formState.errors.kpisUserVfuk48h[0]
											: null
									}
									style={{ backgroundColor: 'white', border: '1px solid #d5d9e0' }}
								/>
							</Grid>
							<Grid item md={2} xs={2}>
								<SelectField
									id="_4gStatusVfuk48h"
									name="_4gStatusVfuk48h"
									hintText="4G Status VFUK (48h)"
									defaultValue=""
									menuItems={
										(dropdowns && dropdowns.okNokStatus) || []
									}
									value={formState.values._4gStatusVfuk48h}
									onChange={_handleChange}
									error={
										_hasError('_4gStatusVfuk48h')
											? formState.errors._4gStatusVfuk48h[0]
											: null
									}
									style={{ backgroundColor: 'white', border: '1px solid #d5d9e0' }}
								/>
							</Grid>
							<Grid item md={2} xs={2}>
								<SelectField
									id="_5gStatusVfuk48h"
									name="_5gStatusVfuk48h"
									hintText="5G Status VFUK (48h)"
									defaultValue=""
									menuItems={
										(dropdowns && dropdowns.okNokStatus) || []
									}
									value={formState.values._5gStatusVfuk48h}
									onChange={_handleChange}
									error={
										_hasError('_5gStatusVfuk48h')
											? formState.errors._5gStatusVfuk48h[0]
											: null
									}
									style={{ backgroundColor: 'white', border: '1px solid #d5d9e0' }}
								/>
							</Grid>
							<Grid item md={3} xs={3}>
								<SelectField
									id="kpisMonitoringStatusVfuk48h"
									name="kpisMonitoringStatusVfuk48h"
									hintText="KPIs Monitoring Status  VFUK (48h)"
									defaultValue=""
									menuItems={
										(dropdowns && dropdowns.okNokStatus) || []
									}
									value={formState.values.kpisMonitoringStatusVfuk48h}
									onChange={_handleChange}
									error={
										_hasError('kpisMonitoringStatusVfuk48h')
											? formState.errors.kpisMonitoringStatusVfuk48h[0]
											: null
									}
									style={{ backgroundColor: 'white', border: '1px solid #d5d9e0' }}
								/>
							</Grid>
							<Grid item md={3} xs={3}>
								<TextField
									id="kpisMonitorVfuk48hObs"
									name="kpisMonitorVfuk48hObs"
									label="KPIs Monitor VFUK (48h) Obs"
									variant="outlined"
									error={_hasError('kpisMonitorVfuk48hObs')}
									fullWidth
									helperText={
										_hasError('kpisMonitorVfuk48hObs')
											? formState.errors.kpisMonitorVfuk48hObs[0]
											: null
									}
									value={formState.values.kpisMonitorVfuk48hObs || ''}
									onChange={_handleChange}
									sx={{
										'& .MuiInputBase-input': {
											backgroundColor: 'white',
											'&.Mui-disabled': {
												color: 'dimGray',
												WebkitTextFillColor: 'dimGray'
											}
										},
									}}
								/>
							</Grid>
						</Grid>
						<br />
						<Grid container spacing={2}>
							<Grid item md={2} xs={2}>
								<SelectField
									id="kpisUser3uk48h"
									name="kPIsMonitoringStatusVmo26h"
									hintText="KPIs User 3UK (48h))"
									defaultValue=""
									menuItems={
										(dropdowns && dropdowns.userName) || []
									}
									value={formState.values.kpisUser3uk48h}
									onChange={_handleChange}
									error={
										_hasError('kpisUser3uk48h')
											? formState.errors.kpisUser3uk48h[0]
											: null
									}
									style={{ backgroundColor: 'white', border: '1px solid #d5d9e0' }}
								/>
							</Grid>
							<Grid item md={2} xs={2}>
								<SelectField
									id="_4GStatus3uk48h"
									name="_4GStatus3uk48h"
									hintText="4G Status 3UK (48h)"
									defaultValue=""
									menuItems={
										(dropdowns && dropdowns._4GStatus3uk48h) || []
									}
									value={formState.values._4GStatus3uk48h}
									onChange={_handleChange}
									error={
										_hasError('_4GStatus3uk48h')
											? formState.errors._4GStatus3uk48h[0]
											: null
									}
									style={{ backgroundColor: 'white', border: '1px solid #d5d9e0' }}
								/>
							</Grid>
							<Grid item md={2} xs={2}>
								<SelectField
									id="_5GStatus3uk48h"
									name="_5GStatus3uk48h"
									hintText="5G Status 3UK (48h)"
									defaultValue=""
									menuItems={
										(dropdowns && dropdowns._5GStatus3uk48h) || []
									}
									value={formState.values._5GStatus3uk48h}
									onChange={_handleChange}
									error={
										_hasError('_5GStatus3uk48h')
											? formState.errors._5GStatus3uk48h[0]
											: null
									}
									style={{ backgroundColor: 'white', border: '1px solid #d5d9e0' }}
								/>
							</Grid>
							<Grid item md={3} xs={3}>
								<SelectField
									id="kpisMonitoringStatus3uk48h"
									name="kpisMonitoringStatus3uk48h"
									hintText="KPIs Monitoring Status  3UK (48h)"
									defaultValue=""
									menuItems={
										(dropdowns && dropdowns.kpisMonitoringStatus3uk48h) || []
									}
									value={formState.values.kpisMonitoringStatus3uk48h}
									onChange={_handleChange}
									error={
										_hasError('kpisMonitoringStatus3uk48h')
											? formState.errors.kpisMonitoringStatus3uk48h[0]
											: null
									}
									style={{ backgroundColor: 'white', border: '1px solid #d5d9e0' }}
								/>
							</Grid>
							<Grid item md={3} xs={3}>
								<TextField
									id="kpisMonitor3uk48hObs"
									name="kpisMonitor3uk48hObs"
									label="KPIs Monitor 3UK (48h) Obs"
									variant="outlined"
									error={_hasError('kpisMonitor3uk48hObs')}
									fullWidth
									helperText={
										_hasError('kpisMonitor3uk48hObs')
											? formState.errors.kpisMonitor3uk48hObs[0]
											: null
									}
									value={formState.values.kpisMonitor3uk48hObs || ''}
									onChange={_handleChange}
									sx={{
										'& .MuiInputBase-input': {
											backgroundColor: 'white',
											'&.Mui-disabled': {
												color: 'dimGray',
												WebkitTextFillColor: 'dimGray'
											}
										},
									}}
								/>
							</Grid>
						</Grid>
						<br />
						<Grid container spacing={2}>
							<Grid item md={12} xs={12}>
								<SiteTrackerComments title="VFUK/3UK Performance Comments" tableId={tableId} comments={comments.vfuk3ukPerformance} />
							</Grid>
						</Grid>
						{/* New row */}
						<br />
						<Grid container spacing={2}>
							<Grid item md={12} xs={12}>
								<Divider><Typography variant="h5" gutterBottom component="div">Attachments</Typography></Divider>
							</Grid>
							<Grid item md={10} xs={10}>
								<TeamsFilesDatatable data={(attachments && attachments.vfuk3ukPerformance) && attachments.vfuk3ukPerformance} tableId={tableId} />
							</Grid>
							<Grid item md={2}>
								<Grid container spacing={2}>
									<Grid item md={12} xs={12}>
										<Button
											className={classes.agreeButton}
											color="primary"
											onClick={() => _onAddAttachmentsClick('vfuk3ukPerformance')}
											variant="contained"
											size="small"
											style={{ width: '100%' }}
										>
											Attach Files
										</Button>
									</Grid>
									{/* <Grid item md={12} xs={12} hidden={attachments && attachments.vfuk3ukPerformance === null}>
										<Button
											className={classes.agreeButton}
											color="primary"
											onClick={() => _onRemoveAttachmentsClick('vfuk3ukPerformance')}
											variant="contained"
											size="small"
											style={{ width: '100%' }}
										>
											Delete All Files
										</Button>
									</Grid> */}
									<Grid item md={12} xs={12}>
										<Button
											className={classes.agreeButton}
											color="primary"
											onClick={() => _onDownloadAttachmentsClick('vfuk3ukPerformance')}
											variant="contained"
											size="small"
											style={{ width: '100%' }}
											disabled={(attachments && attachments.vfuk3ukPerformance) && attachments.vfuk3ukPerformance.length === 0}
										>
											Download All Files
										</Button>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</AccordionDetails>
				</Accordion>
				<Accordion>
					<AccordionSummary
						expandIcon={<ExpandMoreIcon />}
						aria-controls="panel2-content"
						id="panel2-header"
						className='highlight'
					>
						<Typography variant="h5" gutterBottom component="div">Claims Info</Typography>
					</AccordionSummary>
					<AccordionDetails className="backgroundGrey" style={{ paddingTop: '20px' }}>
						<Grid container spacing={2}>
							<Grid item md={2} xs={2}>
								<SelectField
									id="claimsStatusUser"
									name="claimsStatusUser"
									hintText="Claims Status User"
									defaultValue=""
									menuItems={
										(dropdowns && dropdowns.userName) || []
									}
									value={formState.values.claimsStatusUser}
									onChange={_handleChange}
									error={
										_hasError('claimsStatusUser')
											? formState.errors.claimsStatusUser[0]
											: null
									}
									style={{ backgroundColor: 'white', border: '1px solid #d5d9e0' }}
								/>
							</Grid>
							<Grid item md={2} xs={2}>
								<DatePicker
									id="claimsStatusDate"
									name="claimsStatusDate"
									label="Claims Status Date"
									value={formState.values.claimsStatusDate}
									onChange={value => _handleDateChange(value, 'claimsStatusDate')}
									error={
										_hasError('claimsStatusDate')
											? formState.errors.claimsStatusDate[0]
											: null
									}
									helperText={
										_hasError('claimsStatusDate')
											? formState.errors.claimsStatusDate[0]
											: null
									}
									format="DD-MM-YYYY"
								/>
							</Grid>
							<Grid item md={2} xs={2}>
								<SelectField
									id="claimsStatus"
									name="claimsStatus"
									hintText="Claims Status"
									defaultValue=""
									menuItems={
										(dropdowns && dropdowns.claimsStatus) || []
									}
									value={formState.values.claimsStatus}
									onChange={_handleChange}
									error={
										_hasError('claimsStatus')
											? formState.errors.claimsStatus[0]
											: null
									}
									style={{ backgroundColor: 'white', border: '1px solid #d5d9e0' }}
								/>
							</Grid>
							<Grid item md={6} xs={6}>
								<TextField
									id="claimsStatusObs"
									name="claimsStatusObs"
									label="Claims Status Obs"
									variant="outlined"
									error={_hasError('claimsStatusObs')}
									fullWidth
									helperText={
										_hasError('claimsStatusObs')
											? formState.errors.claimsStatusObs[0]
											: null
									}
									value={formState.values.claimsStatusObs || ''}
									onChange={_handleChange}
									sx={{
										'& .MuiInputBase-input': {
											backgroundColor: 'white',
											'&.Mui-disabled': {
												color: 'dimGray',
												WebkitTextFillColor: 'dimGray'
											}
										},
									}}
								/>
							</Grid>
						</Grid>
						<br />
						<Grid container spacing={2}>
							<Grid item md={12} xs={12}>
								<SiteTrackerComments title="Claims Info Comments" tableId={tableId} comments={comments.claimsInfo} />
							</Grid>
						</Grid>
						{/* New row */}
						<br />
						<Grid container spacing={2}>
							<Grid item md={12} xs={12}>
								<Divider><Typography variant="h5" gutterBottom component="div">Attachments</Typography></Divider>
							</Grid>
							<Grid item md={10} xs={10}>
								<TeamsFilesDatatable data={(attachments && attachments.claimsInfo) && attachments.claimsInfo} tableId={tableId} />
							</Grid>
							<Grid item md={2}>
								<Grid container spacing={2}>
									<Grid item md={12} xs={12}>
										<Button
											className={classes.agreeButton}
											color="primary"
											onClick={() => _onAddAttachmentsClick('claimsInfo')}
											variant="contained"
											size="small"
											style={{ width: '100%' }}
										>
											Attach Files
										</Button>
									</Grid>
									{/* <Grid item md={12} xs={12} hidden={attachments && attachments.claimsInfo === null}>
										<Button
											className={classes.agreeButton}
											color="primary"
											onClick={() => _onRemoveAttachmentsClick('claimsInfo')}
											variant="contained"
											size="small"
											style={{ width: '100%' }}
										>
											Delete All Files
										</Button>
									</Grid> */}
									<Grid item md={12} xs={12}>
										<Button
											className={classes.agreeButton}
											color="primary"
											onClick={() => _onDownloadAttachmentsClick('claimsInfo')}
											variant="contained"
											size="small"
											style={{ width: '100%' }}
											disabled={(attachments && attachments.claimsInfo) && attachments.claimsInfo.length === 0}
										>
											Download All Files
										</Button>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</AccordionDetails>
				</Accordion>
			</div>
		</Card >
	);
};

TeamOne.propTypes = {
	className: PropTypes.object,
	tableId: PropTypes.number,
	attachments: PropTypes.object,
	comments: PropTypes.any
};

export default TeamOne;
